<template>
  <!-- IMPORTANT: avoid rendering while loading content -->
  <!-- This prevents children components from calling setup() without prefilled data -->
  <div class="pt-5 pb-1 main position-fixed bottom-0 end-0 start-0">
    <LoadingOverlay :loading="loading" />
  </div>
  <div class="main" v-if="!loading && allowCardShipments">
    <Header
      v-if="currentStep !== 'ADDRESS' && showfullUserDataFrom"
      :screenTitle="t('PAYJOY_CARD_VIEWS.SHIPPING_FORM_TITLE')"
      :navbarType="'goBack'"
      :showProgressBar="true"
      :progressPercentage="progressPercentage"
      @clicked="handleBackClick"
    />
    <UserPhoneConfirmationForm
      v-if="currentStep === 'USER_DATA' && !showfullUserDataFrom"
      :showBanner="true"
      :preFilledData="userFormData"
      :title="t('PAYJOY_CARD_VIEWS.USER_PHONE_CONFIRMATION_FORM_TITLE')"
      :description="
        t('PAYJOY_CARD_VIEWS.USER_PHONE_CONFIRMATION_FORM_DESCRIPTION')
      "
      @onContinueClick="handleUseDataForm"
    />
    <UserDataForm
      v-if="currentStep === 'USER_DATA' && showfullUserDataFrom"
      :preFilledData="userFormData"
      @onContinueClick="handleUseDataForm"
    />
    <NewShippingAddressForm
      :source="NewFormSource.Application"
      v-if="currentStep === 'ADDRESS' && shouldUseNewShippingUI"
      @onContinueClick="handleAddressForm"
      @goBackClick="goBackClick"
    />
    <ShippingAddressForm
      v-if="currentStep === 'ADDRESS' && !shouldUseNewShippingUI"
      @onContinueClick="handleAddressForm"
      @onCancelClick="handleBackClick"
      :preFilledData="userFormData"
      @goBackClick="goBackClick"
    />
    <ContactSupportModal
      v-if="isErrorModalOpen"
      :modalHeaderImage="
        useAsset('img/credit-line/credit-card/shipmentError.png')
      "
      :modalHeaderTitle="
        t('PAYJOY_CARD_VIEWS.RESULT_SHIPPING_FORM.PROBLEMS_SENDING_INFORMATION')
      "
      :modalHeaderSubtitle="
        t('PAYJOY_CARD_VIEWS.RESULT_SHIPPING_FORM.CONFIRM_DELIVERY_DETAILS')
      "
      :showCallCxButton="false"
      @modalAction="openWhatsApp"
      @modalAction2="resultDismissFunc"
    />
    <FullScreenModal
      :isOpen="isModalOpen"
      @onClose="closeResult"
      class="pt-5 pb-1"
    >
      <CreditLineApiErrorDisplay
        v-if="resultError"
        :custom-dismiss="true"
        @onDismiss="resultDismissFunc"
      >
        <Button id="wp-btn" class="text-center mb-3" @click="openWhatsApp()">
          <p class="text-center text-white my-auto">
            {{ t("PAYJOY_CARD_VIEWS.RESULT_SHIPPING_FORM.WHATSAPP") }}
          </p>
        </Button>
      </CreditLineApiErrorDisplay>
      <VerificationResult
        v-else
        :icon="resultIcon"
        :title="resultTitle"
        :description="resultDescription"
        :btnText="resultBtnText"
        @dismissFunc="resultDismissFunc"
      >
        <div class="w-100 mt-auto mb-0">
          <Button
            id="wp-btn"
            class="text-center p-3 mb-3"
            @click="openWhatsApp()"
          >
            <p class="text-center text-white fw-bold my-auto">
              {{ t("PAYJOY_CARD_VIEWS.RESULT_SHIPPING_FORM.WHATSAPP") }}
            </p>
          </Button>
          <Button class="text-center p-3" @click="resultDismissFunc()">
            <p class="text-center text-white fw-bold my-auto">
              {{ resultBtnText }}
            </p>
          </Button>
        </div>
      </VerificationResult>
    </FullScreenModal>
  </div>
  <div v-if="!allowCardShipments && !loading">
    <DownUpModal
      modal-height="45%"
      :modal-header-image="redWarningIcon"
      :modal-header-title="t('VIRTUAL_CARD_CREATION_NOT_ALLOWED_MODAL.TITLE')"
      :modal-header-subtitle="
        t('VIRTUAL_CARD_CREATION_NOT_ALLOWED_MODAL.DESCRIPTION')
      "
      :btn-text="t('GOHOME')"
      @modalAction="goHome"
    />
  </div>
</template>

<script setup lang="ts">
import {
  getCurrentInstance,
  ref,
  watch,
  onMounted,
  onUnmounted,
  computed,
  provide,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import useAsset from "@/apps/credit-line/helpers/useAsset";

import Header from "@/shared-components/consumer/Header.vue";
import ShippingAddressForm from "@/apps/credit-line/components/CreditCard/ShippingAddress/ShippingAddressForm.vue";
import UserDataForm from "@/apps/credit-line/components/CreditCard/UserDataForm.vue";
import UserPhoneConfirmationForm from "@/apps/credit-line/components/CreditCard/UserPhoneConfirmationForm.vue";
import FullScreenModal from "@/apps/credit-line/components/FullScreenModal.vue";
import LoadingOverlay from "@/shared-components/ui/core-app/LoadingOverlay.vue";
import VerificationResult from "@/apps/credit-line/components/Result.vue";
import { Service } from "@/apps/credit-line/services/network.service";
import NewShippingAddressForm from "@/apps/credit-line/components/CreditCard/ShippingAddress/NewShippingAddressForm.vue";
import DatadogLogger from "@/shared-helpers/datadog-logger.helper";
import { LogType } from "@/types/dataDogLogType";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import { recorder, RECORDING_APPS } from "@/shared-services/recording.service";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";
import { getCookieHelper } from "@/shared-helpers/get-cookie.helper";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import {
  ICreditCardAddressFormData,
  ICreditCardUserFormData,
} from "@/apps/credit-line/types/forms";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import Button from "@/apps/credit-line/components/Button.vue";
import { AxiosError } from "axios";
import { safeDatadogRum } from "@/apps/credit-line/helpers/safeDatadogRum.helper";
import { getRandomNumberBetweenRange } from "@/apps/credit-line/helpers/misc.helper";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";
import CreditLineApiErrorDisplay from "@/apps/credit-line/views/CreditLineApiErrorDisplay.vue";
import {
  AvailableFeatures,
  FeatureControlService,
} from "@/apps/credit-line/services/feature-control.service";
import ContactSupportModal from "@/apps/credit-line/components/ContactSupportModal.vue";
import { getSettings } from "@/apps/credit-line/helpers/consumerSettings.helper";
import {
  DEFAULT_SETTING,
  SettingValue,
} from "@/shared-models/consumer/ISetting";
import { NewFormSource } from "../../components/CreditCard/ShippingAddress/types";
import { useStore } from "vuex";
import { SHIPMENT_FLOW_TYPE } from "../../types/shipmentsType";
import { SettingsCategory } from "@/shared-models/consumer/SettingsCategory";
import DownUpModal from "@/apps/credit-line/components/DownUpModal.vue";
import { safeAndroidCall } from "@/apps/credit-line/helpers/safe-android-call.helper";

declare const Android: AndroidInterface;

// Composables
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const { fetchSummary } = useCreditLineSummary();

// Services
const CreditLineService = new Service();
const featureService = new FeatureControlService();

// Reactive data
const deviceTag = ref<string>(AuthenticationHelper.getDeviceTag() as string);
const userFormData = ref<ICreditCardUserFormData | null>(null);
const currentStep = ref<"USER_DATA" | "USER_DATA_MISSING" | "ADDRESS">(
  "USER_DATA",
);
const isModalOpen = ref<boolean>(false);
const loading = ref<boolean>(true);
const showKyc = ref<boolean>(false);
const progressPercentage = ref<number>(35);
const validationFailed = ref<boolean>(false);
const preFilledCity = ref<string>("");
const preFilledRegion = ref<string>("");
const isKeyboardOpen = ref<boolean>(false);
const isErrorModalOpen = ref<boolean>(false);

// Result modal data
const resultIcon = ref<string>();
const resultTitle = ref<string>();
const resultDescription = ref<string>();
const resultBtnText = ref<string>();
const resultError = ref<boolean>(false);
const showfullUserDataFrom = ref<boolean>(false);
const creditLineId = ref<number>();
const country = ref<string>("");
const store = useStore();

//Configurability
const isGoogleAutocompleteEnabled = ref<SettingValue>(DEFAULT_SETTING);
const isNewShipmentsUiEnabled = ref<SettingValue>(DEFAULT_SETTING);
const prefix = ref<string>("");
const shipmentFormConfiguration = ref<SettingValue>(DEFAULT_SETTING);
const allowCardShipments = ref<boolean>(false);
const redWarningIcon = useAsset("img/credit-line/red_warning_icon.png");
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const shipmentFieldsConfiguration = ref<Record<string, any>>({});
const coverageValidation = ref<string>("");
const autocompleteProvider = ref<string>("");
const autocompleteCountryCode = ref<string>("");

const getFormSettings = async () => {
  try {
    const cardsSettings = await getSettings(SettingsCategory.CL_CARDS_PAGE);
    allowCardShipments.value =
      cardsSettings.allowCardShipments?.enabled ?? false;
    isGoogleAutocompleteEnabled.value = cardsSettings.useGoogleAutocomplete;
    isNewShipmentsUiEnabled.value = cardsSettings.useNewShipmentsUi;
    shipmentFormConfiguration.value = cardsSettings.shipment_forms;
    shipmentFieldsConfiguration.value =
      shipmentFormConfiguration.value.values.formValuesRules;
    prefix.value =
      shipmentFormConfiguration.value.values.formValuesRules.phoneNumberCountryCode;
    coverageValidation.value =
      shipmentFormConfiguration.value.values.formValuesRules.coverageValidator;
    autocompleteProvider.value = cardsSettings.useSitiDataAutocomplete.enabled
      ? "sitiData"
      : "googlePlaces";
    autocompleteCountryCode.value =
      shipmentFormConfiguration.value.values.formValuesRules.country;
  } catch (error) {
    console.error("Error fetching settings:", error);
  }
};

const shouldUseNewShippingUI = computed(() => {
  return isNewShipmentsUiEnabled.value?.enabled ?? true;
});

const shouldUseGoogleAutocomplete = computed(() => {
  return isGoogleAutocompleteEnabled.value?.enabled ?? false;
});

//Provides
provide("creditLineId", creditLineId);
provide("deviceTag", deviceTag);
provide("isGoogleAutocompleteEnabled", shouldUseGoogleAutocomplete);
provide("preFilledRegionValue", preFilledRegion);
provide("preFilledCityValue", preFilledCity);
provide("shipmentFieldsConfiguration", shipmentFieldsConfiguration);
provide("prefix", prefix);
provide("coverageValidation", coverageValidation);
provide("autocompleteProvider", autocompleteProvider);
provide("autocompleteCountryCode", autocompleteCountryCode);

// Methods
const openWhatsApp = () => {
  const helpMsg = t("CL_WP_HELP_MSG", {
    deviceTag: AuthenticationHelper.getDeviceTag(),
  });
  const wpNumber = featureService.getFeatureValue(
    AvailableFeatures.WHATSAPP,
    country.value,
    "number",
  );
  window.open(`https://wa.me/${wpNumber}?text=` + helpMsg, "_system");
};

const hideNav = () => {
  if (Android && typeof Android?.hideNav === "function") {
    Android.hideNav();
  }
};

const handleBackClick = () => {
  if (currentStep.value === "ADDRESS") {
    currentStep.value = "USER_DATA";
    progressPercentage.value = 66;
    return;
  }
  if (isAndroid()) {
    router.back();
  } else {
    window.close();
  }
};

const handleUseDataForm = (data: ICreditCardUserFormData) => {
  userFormData.value = data;
  currentStep.value = "ADDRESS";
  progressPercentage.value = 100;
};

const goHome = () => {
  safeAndroidCall("showNav");
  safeAndroidCall("dismissWebView");
};

const showAddressResultError = () => {
  isModalOpen.value = true;
  resultIcon.value = useAsset("img/credit-line/error_warning_icon.svg");
  resultTitle.value = t(
    "PAYJOY_CARD_VIEWS.RESULT_SHIPPING_FORM.ERROR_SOMETHING_GOES_WRONG",
  );
  resultDescription.value = t(
    "PAYJOY_CARD_VIEWS.RESULT_SHIPPING_FORM.ERROR_TRY_AGAIN",
  );
  resultBtnText.value = t("PAYJOY_CARD_VIEWS.RESULT_SHIPPING_FORM.TRY_AGAIN");
  resultError.value = true;
};

const handleTaxIdentificationValue = (country: string) => {
  if (country === "CO") {
    return userFormData.value?.taxIdentificationValue || "";
  } else {
    return userFormData.value?.taxIdentificationValue + "3" || "";
  }
};

const handleIdentificationValue = (country: string) => {
  if (country === "CO") {
    return userFormData.value?.identificationValue || "";
  } else {
    return userFormData.value?.identificationValue + "3" || "";
  }
};

const getInitialData = async () => {
  loading.value = true;
  try {
    return await fetchSummary();
  } catch (error) {
    return null;
  }
};

const handleAddressForm = async (data: ICreditCardAddressFormData) => {
  const summary = await getInitialData();
  loading.value = true;
  const reportCardOptions =
    store.getters["creditLine/getReportLostOrDamagedCard"];

  const shippingType = reportCardOptions?.reason
    ? SHIPMENT_FLOW_TYPE.RENEW_SHIPMENT
    : SHIPMENT_FLOW_TYPE.FIRST_SHIPMENT;

  safeDatadogRum.addAction("shipment_form_submission_started", {
    deviceTag: deviceTag.value,
    creditLineId: creditLineId.value,
    country: summary?.country,
    shippingType,
  });

  try {
    const response = await CreditLineService.shipCreditCard({
      deviceTag: deviceTag.value,
      firstName: userFormData.value?.firstName || "",
      lastName: userFormData.value?.lastName || "",
      birthday: userFormData.value?.birthday || "",
      email: userFormData.value?.email || "",
      gender: userFormData.value?.gender || "",
      taxIdentificationValue: handleTaxIdentificationValue(
        summary?.country || "",
      ),
      identificationValue: handleIdentificationValue(summary?.country || ""),
      phone: userFormData.value?.phoneNumber || "",
      streetName: data.street,
      streetNumber: data.streetNumber,
      floor: summary?.country === "CO" ? "" : data.floor,
      apartment: data.apartment,
      city: data.city,
      zip: data.postalCode,
      region: data.region,
      neighborhood: data.neighborhood,
      additionalInfo: data.notes || "N/A",
      country: summary?.country || "",
      isZipCodeUserProvided: data.isZipCodeUserProvided,
      source: "app",
      isProposedAddressSelected: data.isProposedAddressSelected ?? false,
      shippingAttemptId: data.shippingAttemptId,
      isGoogleAutocompleteShown: data.isGoogleAutocompleteShown ?? false,
      autocompleteProvider: data.autocompleteProvider ?? "googlePlaces",
      addressScore: data.addressScore ?? "",
      shippingType,
      reportCardOption: reportCardOptions?.lastFour
        ? {
            reason: reportCardOptions?.reason || "",
            lastFour: reportCardOptions?.lastFour || 0,
            details: reportCardOptions?.details || "",
          }
        : null,
    });

    const result = response.data ?? response;
    if (result.isZoneWithCoverage === false) {
      isErrorModalOpen.value = true;
      safeDatadogRum.addError(new Error("Shipment zone has no coverage"), {
        source: "shipment_form_submission",
        deviceTag: deviceTag.value,
        creditLineId: creditLineId.value,
        country: summary?.country,
        shippingType,
        errorType: "no_coverage",
      });
      DatadogLogger.Log(
        "Credit card shipping form network error",
        {
          deviceTag: deviceTag.value,
        },
        LogType.ERROR,
      );
    } else {
      safeDatadogRum.addAction("shipment_form_submission_success", {
        deviceTag: deviceTag.value,
        creditLineId: creditLineId.value,
        country: summary?.country,
        shippingType,
      });
      DatadogLogger.Log(
        "Credit card shipping form successfully sent",
        {
          deviceTag: deviceTag.value,
        },
        LogType.INFO,
      );
      const name = "CreditLineResultView";
      const query = {
        success: 1,
      };
      router.push(
        getCookieHelper("dashboardUser")
          ? { name, query: { ...query, fromDashboard: 1 } }
          : { name, query },
      );
    }
  } catch (err) {
    const error = err as AxiosError;
    safeDatadogRum.addError(error, {
      source: "shipment_form_submission",
      deviceTag: deviceTag.value,
      creditLineId: creditLineId.value,
      country: summary?.country,
      shippingType,
      errorType: error?.response?.data?.errorCode
        ? "api_error"
        : "network_error",
      errorCode: error?.response?.data?.errorCode,
      errorMessage: error?.message,
    });

    if (error.response && error.response.data?.errorCode) {
      ErrorHandler.logCreditlineErrorResponse(error.response);
      DatadogLogger.Log(
        "Credit card shipping form error",
        {
          deviceTag: deviceTag.value,
        },
        LogType.ERROR,
      );
      showAddressResultError();
    } else {
      ErrorHandler.handleNetworkErrorAndLog(error);
      DatadogLogger.Log(
        "Credit card shipping form network error",
        {
          deviceTag: deviceTag.value,
        },
        LogType.ERROR,
      );
      showAddressResultError();
    }
  } finally {
    loading.value = false;
  }
};

const goBackClick = () => {
  currentStep.value = "USER_DATA";
  progressPercentage.value = 66;
};

const resultDismissFunc = () => {
  if (resultError.value && validationFailed.value) {
    router.replace({ name: "CreditLineHelpView" });
    return;
  } else if (!resultError.value) {
    showKyc.value = false;
    currentStep.value = "USER_DATA";
    progressPercentage.value = 66;
  }

  isModalOpen.value = false;
};

const closeResult = () => {
  if (validationFailed.value) {
    router.back();
    return;
  }
  if (!validationFailed.value && showKyc.value) {
    showKyc.value = false;
    currentStep.value = "USER_DATA";
    progressPercentage.value = 66;
  }
  isModalOpen.value = false;
};

// Watchers
watch(isKeyboardOpen, (newValue) => {
  if (!newValue) {
    hideNav();
  }
});

// Lifecycle hooks
onMounted(async () => {
  loading.value = true;

  await getFormSettings();

  if (!allowCardShipments.value) {
    safeAndroidCall("hideNav");
  }

  const handleResize = () => {
    isKeyboardOpen.value = window.innerHeight < screen.height * 0.7;
  };

  window.addEventListener("resize", handleResize);
  handleResize();
  const app = getCurrentInstance()?.appContext.app;
  recorder(app)?.startRecording(RECORDING_APPS.CREDIT_LINE);
  recorder(app)?.updateRecordingInfo(getRandomNumberBetweenRange(1000000, 1), {
    deviceTag: Android.getDeviceTag(),
  });

  // Use query params if available, otherwise fetch summary
  if (
    route.query?.creditLineId &&
    route.query?.firstName &&
    route.query?.lastName &&
    route.query?.idNumber &&
    route.query?.gender &&
    route.query?.birthDate &&
    route.query?.email
  ) {
    creditLineId.value = parseInt(route.query.creditLineId as string);
    userFormData.value = {
      birthday: route.query?.birthDate as string,
      email: route.query?.email as string,
      gender: route.query?.gender as string,
      firstName: route.query?.firstName as string,
      lastName: route.query?.lastName as string,
      identificationValue: route.query?.idNumber as string,
      phoneNumber: "",
      taxIdentificationValue: "",
      source: "route",
    };
  } else {
    const summary = await getInitialData();
    country.value = summary?.country || "";
    creditLineId.value = summary?.creditLineId;
    if (summary?.creditLineId) {
      userFormData.value = {
        birthday: summary?.user?.birthDate || "",
        email: summary?.user?.email || "",
        gender: summary?.user?.gender || "",
        firstName: summary?.user?.firstName || "",
        lastName: summary?.user?.lastName || "",
        identificationValue: summary?.user?.idNumber || "",
        phoneNumber: summary?.user?.phone,
        taxIdentificationValue: "",
        source: "fetch",
      };
    }
  }

  if (userFormData.value) {
    for (let [key, value] of Object.entries(userFormData.value)) {
      if (
        key != "phoneNumber" &&
        key != "taxIdentificationValue" &&
        value == ""
      ) {
        showfullUserDataFrom.value = true;
      }
    }
  }

  if (isAndroid()) {
    Android.finishedLoading();
  }
  loading.value = false;
});

onUnmounted(() => {
  const handleResize = () => {
    isKeyboardOpen.value = window.innerHeight < screen.height * 0.7;
  };
  window.removeEventListener("resize", handleResize);
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/constants";
.step-component {
  height: 100%;
  overflow-y: hidden;
  position: relative !important;
}

#wp-btn {
  background: #46b871;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);

  &:hover {
    background-color: darken($pj-color-green, 10%);
  }
}
</style>
